import type { ManualDoc } from "../types.ts"
import { t } from "./t.ts"

export const REFERENCE_DOCS: ManualDoc[] = [
  {
    slug: "statuses",
    category: "reference",
    order: 1,
    title: t("상태값 한눈에", "状態の一覧"),
    summary: t(
      "반품 건·서류·선적이 가질 수 있는 상태와, 누가 그 상태를 만드는지.",
      "返品・書類・船積みが取りうる状態と、誰がその状態にするか。",
    ),
    keywords: ["상태", "접수완료", "입고완료", "서류완료", "출고", "반송완료", "状態", "ステータス"],
    body: [
      { kind: "heading", text: t("반품 건의 상태", "返品の状態") },
      {
        kind: "table",
        head: [t("상태", "状態"), t("뜻", "意味"), t("누가 바꾸나", "変える人")],
        rows: [
          [t("접수완료", "受付完了"), t("등록됐고 상자는 아직 일본에 오지 않았다.", "登録済みで、箱はまだ日本に届いていない。"), t("화주(등록)", "荷主(登録)")],
          [t("입고완료", "入庫完了"), t("상자가 일본 창고에 들어왔다.", "箱が日本の倉庫に届いた。"), t("쿨트랙JP(스캔·손 입고)", "日本側(スキャン・手入力)")],
          [t("서류완료", "書類完了"), t("처리방안과 인보이스 품목이 확정됐다. 선적에 실을 수 있다.", "処理方針とインボイス品目が確定した。船積み可能。"), t("화주(서류 확정)", "荷主(書類の確定)")],
          [t("출고", "出庫"), t("선적에 실려 도쿄에서 나갔다.", "船積みに載り、東京から出発した。"), t("쿨트랙JP(출고 처리)", "日本側(出庫処理)")],
          [t("반송완료", "返送完了"), t("한국 지정창고에 들어왔다. 종결.", "韓国の指定倉庫に到着。完了。"), t("화주(한국 입고 처리)", "荷主(韓国入庫)")],
          [t("폐기", "廃棄"), t("일본에서 버렸다. 종결.", "日本で廃棄した。完了。"), t("쿨트랙JP(폐기 처리)", "日本側(廃棄処理)")],
          [t("접수취소", "受付取消"), t("입고 전에 접수를 취소했다. 종결.", "入庫前に受付を取り消した。完了。"), t("화주(접수 취소)", "荷主(受付取消)")],
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "상태는 되돌아가지 않습니다. 잘못 찍었다면 이력에 남겨 두고 다음 단계에서 바로잡습니다 — 되돌리는 길을 열어 두면 지금 어디까지 왔는지를 아무도 믿을 수 없게 됩니다.",
          "状態は前に戻りません。誤って進めた場合は履歴に残したうえで次の段階で修正します — 戻せるようにすると、今どこまで進んだのか誰も信用できなくなります。",
        ),
      },
      { kind: "heading", text: t("서류작성여부", "書類作成状況") },
      {
        kind: "table",
        head: [t("값", "値"), t("뜻", "意味")],
        rows: [
          [t("미기재중", "未記入"), t("인보이스 품목이 하나도 없다.", "インボイス品目がまだない。")],
          [t("인보이스 항목기재", "インボイス品目記入"), t("쿨트랙JP가 품목을 적었다. 화주 확인을 기다린다.", "日本側が品目を記入した。荷主の確認待ち。")],
          [t("항목확인요청", "項目確認依頼"), t("화주가 다시 봐 달라고 했다. 쿨트랙JP가 고쳐야 한다.", "荷主が再確認を依頼した。日本側の修正が必要。")],
          [t("확인완료", "確認完了"), t("서류가 확정됐다.", "書類が確定した。")],
        ],
      },
      { kind: "heading", text: t("선적의 상태", "船積みの状態") },
      {
        kind: "table",
        head: [t("상태", "状態"), t("뜻", "意味")],
        rows: [
          [t("준비", "準備"), t("건을 묶었고 아직 나가지 않았다.", "件をまとめたが、まだ出ていない。")],
          [t("출고", "出庫"), t("세이노로 도쿄에서 나갔다.", "西濃で東京から出発した。")],
          [t("오사카 도착", "大阪到着"), t("오사카 창고에 들어왔다.", "大阪の倉庫に到着した。")],
          [t("해상 운송중", "海上輸送中"), t("팬스타 배로 한국을 향한다. 한국 입고를 기다린다.", "パンスターの船で韓国へ向かっている。韓国入庫待ち。")],
          [t("반송완료", "返送完了"), t("한국 지정창고에 들어왔다.", "韓国の指定倉庫に到着した。")],
        ],
      },
    ],
  },

  {
    slug: "roles",
    category: "reference",
    order: 2,
    title: t("사용자별 권한", "ユーザーごとの権限"),
    summary: t(
      "역할마다 할 수 있는 일이 다릅니다. 내 화면에 없는 단추는 내 몫이 아닙니다.",
      "役割ごとにできることが異なります。自分の画面にないボタンは自分の担当ではありません。",
    ),
    keywords: ["권한", "역할", "화주", "쿨트랙JP", "관리자", "権限", "役割", "荷主"],
    body: [
      {
        kind: "table",
        head: [t("역할", "役割"), t("누구", "誰"), t("보는 범위", "見える範囲")],
        rows: [
          [t("화주", "荷主"), t("화주 담당자", "荷主の担当者"), t("자기 화주의 건만", "自分の荷主の件のみ")],
          [t("쿨트랙JP", "日本側"), t("일본 창고 담당자", "日本倉庫の担当者"), t("모든 화주의 건", "すべての荷主の件")],
          [t("관리자", "管理者"), t("시스템 관리자", "システム管理者"), t("전부", "すべて")],
        ],
      },
      { kind: "heading", text: t("누가 무엇을 하나", "誰が何をするか") },
      {
        kind: "table",
        head: [t("하는 일", "作業"), t("화주", "荷主"), t("쿨트랙JP", "日本側"), t("관리자", "管理者")],
        rows: [
          [t("반품 접수 등록·수정·취소", "返品の受付登録・修正・取消"), t("O", "O"), t("—", "—"), t("O", "O")],
          [t("입고 스캔 · 손 입고", "入庫スキャン・手入力入庫"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("중량 · 착/선불 · 착불비", "重量・着払い/元払い・着払い費用"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("검수(반품 상품 수량)", "検品(返品商品の数量)"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("입고 내용 확인 · 처리방안", "入庫内容の確認・処理方針"), t("O", "O"), t("—", "—"), t("O", "O")],
          [t("인보이스 품목 기재", "インボイス品目の記入"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("서류 확정 · 항목 확인요청", "書類の確定・項目の確認依頼"), t("O", "O"), t("—", "—"), t("O", "O")],
          [t("선적 만들기 · 출고 · 오사카 · 일본 출항", "船積み作成・出庫・大阪・日本出航"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("한국 입고 처리", "韓国入庫の処理"), t("O", "O"), t("—", "—"), t("O", "O")],
          [t("폐기 처리", "廃棄処理"), t("—", "—"), t("O", "O"), t("O", "O")],
          [t("품목 마스터 추가·수정", "品目マスタの追加・編集"), t("—", "—"), t("—", "—"), t("O", "O")],
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "적는 쪽과 확인하는 쪽을 일부러 갈라 두었습니다. 실물을 든 쿨트랙JP가 세고 적고, 주문을 아는 화주가 확인합니다.",
          "記入する側と確認する側を意図的に分けています。現物を持つ日本側が数えて記入し、注文を知る荷主が確認します。",
        ),
      },
      {
        kind: "p",
        text: t(
          "화주 계정에게 남의 화주 건은 아예 없는 것처럼 보입니다. 주소를 직접 쳐서 열어도 찾을 수 없다고 나옵니다.",
          "荷主アカウントには他の荷主の件は存在しないように見えます。URLを直接入力して開いても「見つかりません」と表示されます。",
        ),
      },
    ],
  },

  {
    slug: "notifications",
    category: "reference",
    order: 3,
    title: t("알림", "通知"),
    summary: t(
      "놓치면 곤란한 다섯 가지를 종 모양 알림으로 알려 줍니다.",
      "見落とすと困る5つをベルの通知で知らせます。",
    ),
    keywords: ["알림", "벨", "읽음", "通知", "ベル", "既読"],
    body: [
      {
        kind: "p",
        text: t(
          "위쪽 줄의 종 모양을 누르면 지금 손이 필요한 일이 모여 있습니다. 안 읽은 것이 있으면 빨간 숫자가 붙습니다.",
          "上部のベルを押すと、今対応が必要な事項がまとまっています。未読があれば赤い数字が付きます。",
        ),
      },      {
        kind: "image",
        src: t("/help/notifications-ko.png", "/help/notifications-ja.png"),
        alt: t("알림 판", "通知パネル"),
        caption: t(
          "안 읽은 알림은 왼쪽에 파란 막대가 서고 글씨가 굵습니다. 누르면 그 조건의 목록으로 갑니다.",
          "未読の通知は左に青いバーが付き、文字が太くなります。押すとその条件の一覧に移動します。",
        ),
      },

      {
        kind: "table",
        head: [t("알림", "通知"), t("언제", "いつ"), t("받는 쪽", "受け取る側")],
        rows: [
          [t("미입고 경고", "未入庫アラート"), t("접수 후 14일이 지나도록 상자가 오지 않았을 때", "受付から14日経っても箱が届かないとき"), t("쿨트랙JP", "日本側")],
          [t("항목확인요청", "項目確認依頼"), t("화주가 인보이스 품목을 다시 봐 달라고 했을 때", "荷主がインボイス品目の再確認を求めたとき"), t("쿨트랙JP", "日本側")],
          [t("수량 불일치", "数量不一致"), t("검수 수량이 등록한 반품수량과 다를 때", "検品数量が登録した返品数量と異なるとき"), t("화주", "荷主")],
          [t("서류 확인 대기", "書類確認待ち"), t("쿨트랙JP가 품목을 적어 확인을 기다릴 때", "日本側が品目を記入して確認を待っているとき"), t("화주", "荷主")],
          [t("한국 입고 대기", "韓国入庫待ち"), t("배가 떠서 한국 도착을 기다릴 때", "船が出て韓国到着を待っているとき"), t("화주", "荷主")],
        ],
      },
      {
        kind: "list",
        items: [
          t("알림을 누르면 그 조건으로 걸러진 반품 목록으로 가고, 읽음으로 바뀝니다.", "通知を押すとその条件で絞り込んだ返品一覧に移動し、既読になります。"),
          t("모두 읽음으로 한 번에 정리할 수 있습니다. 읽은 알림도 목록에는 남습니다.", "「すべて既読」で一度に整理できます。既読の通知も一覧には残ります。"),
          t("읽음은 사람마다 따로입니다. 다른 사람이 읽어도 내 알림은 그대로 남습니다.", "既読は人ごとに別です。他の人が読んでも自分の通知は残ります。"),
          t(
            "같은 상황이 이어져도 알림은 하나로 유지되고, 건수는 그날 기준으로 갱신됩니다.",
            "同じ状況が続いても通知は1つにまとまり、件数はその日の時点で更新されます。",
          ),
        ],
      },
    ],
  },

  {
    slug: "faq",
    category: "reference",
    order: 4,
    title: t("자주 묻는 질문", "よくある質問"),
    summary: t("막혔을 때 먼저 보는 곳.", "困ったときに最初に見るところ。"),
    keywords: ["faq", "질문", "문제", "오류", "안됨", "질문과 답", "よくある質問", "エラー"],
    body: [
      { kind: "heading", text: t("반송장번호를 모르는데 접수해도 되나요?", "返送伝票番号が分からなくても受付できますか?") },
      {
        kind: "p",
        text: t(
          "됩니다. 비워 두고 등록하세요. 다만 번호가 없으면 스캔으로 찾을 수 없으니, 알게 되면 접수 내용을 고쳐 채우거나 쿨트랙JP가 손으로 입고 처리를 하며 채웁니다.",
          "できます。空欄のまま登録してください。ただし番号がないとスキャンで見つけられないため、分かった時点で受付内容を修正するか、日本側が手入力で入庫する際に入力します。",
        ),
      },
      { kind: "heading", text: t("스캔했는데 반품 건이 없다고 나옵니다.", "スキャンしたのに該当する返品がないと出ます。") },
      {
        kind: "p",
        text: t(
          "아직 접수되지 않았거나 접수 때 번호를 비워 둔 건입니다. 반품 목록에서 주문번호나 고객명으로 찾아 손으로 입고 처리를 하세요.",
          "まだ受付されていないか、受付時に番号が空欄の件です。返品一覧で注文番号や顧客名から探し、手入力で入庫してください。",
        ),
      },
      { kind: "heading", text: t("서류 확정 단추가 눌리지 않습니다.", "書類の確定ボタンが押せません。") },
      {
        kind: "p",
        text: t(
          "단추 옆에 막힌 이유가 적혀 있습니다. 처리방안을 정하지 않았거나, 검수가 끝나지 않았거나, 인보이스 품목이 비었거나, 항목 확인요청이 걸려 있는 경우입니다.",
          "ボタンの横に理由が表示されます。処理方針が未定、検品が未完了、インボイス品目が空、項目の確認依頼中、のいずれかです。",
        ),
      },
      { kind: "heading", text: t("검수 수량이 등록과 다릅니다. 어떻게 하나요?", "検品数量が登録と違います。どうすればよいですか?") },
      {
        kind: "p",
        text: t(
          "센 대로 저장하세요. 검수는 사실을 적는 일입니다. 다른 점은 이력에 남고 화주에게 수량 불일치로 알려집니다. 어떻게 처리할지는 화주가 정합니다.",
          "数えたとおりに保存してください。検品は事実を記録する作業です。相違は履歴に残り、荷主に数量不一致として通知されます。対応は荷主が決めます。",
        ),
      },
      { kind: "heading", text: t("인보이스는 누가 적나요?", "インボイスは誰が記入しますか?") },
      {
        kind: "p",
        text: t(
          "상자를 열어 실물을 본 쿨트랙JP가 적고, 주문을 아는 화주가 확인해 확정합니다.",
          "箱を開けて現物を見た日本側が記入し、注文を知る荷主が確認して確定します。",
        ),
      },
      { kind: "heading", text: t("접수 내용을 잘못 적었습니다.", "受付内容を間違えて入力しました。") },
      {
        kind: "p",
        text: t(
          "입고 전이라면 반품 상세에서 고칠 수 있고, 아예 취소하려면 접수 취소를 누릅니다. 입고된 뒤에는 연락처와 비고만 고칠 수 있습니다 — 나머지 값은 이미 인보이스에 실리기 때문입니다.",
          "入庫前であれば返品詳細で修正でき、取り消す場合は受付取消を押します。入庫後は連絡先と備考のみ修正できます — 他の値はすでにインボイスに載るためです。",
        ),
      },
      { kind: "heading", text: t("한국 입고는 왜 우리 화면에 안 보이나요?", "韓国入庫がなぜ自分の画面に出ないのですか?") },
      {
        kind: "p",
        text: t(
          "물건이 한국 창고에 들어온 사실은 한국 쪽만 알기 때문에 그 단계는 화주 계정만 찍을 수 있습니다. 쿨트랙JP 화면에는 한국 도착을 기다리는 중이라고만 보입니다.",
          "荷物が韓国の倉庫に入った事実は韓国側しか分からないため、その段階は荷主のアカウントのみ記録できます。日本側の画面には「韓国到着待ち」とだけ表示されます。",
        ),
      },
      { kind: "heading", text: t("비밀번호를 잊었습니다.", "パスワードを忘れました。") },
      {
        kind: "p",
        text: t(
          "화면에서는 되돌릴 수 없습니다. 관리자에게 재발급을 요청하세요. 비밀번호를 바꾸면 그 계정의 모든 기기에서 로그아웃됩니다.",
          "画面からは戻せません。管理者に再発行を依頼してください。パスワードを変更すると、そのアカウントの全端末からログアウトされます。",
        ),
      },
      { kind: "heading", text: t("화면을 일본어로 보고 싶습니다.", "画面を日本語で見たいです。") },
      {
        kind: "p",
        text: t(
          "내 계정에서 화면 언어를 日本語로 고르면 바로 바뀝니다. 위쪽 계정 메뉴에서도 바꿀 수 있습니다.",
          "アカウントで画面の言語を日本語に選ぶとすぐ切り替わります。上部のアカウントメニューからも変更できます。",
        ),
      },
      { kind: "heading", text: t("이 매뉴얼에 없는 기능을 쓰고 싶습니다.", "このマニュアルにない機能を使いたいです。") },
      {
        kind: "p",
        text: t(
          "이 매뉴얼은 지금 시스템에 있는 기능만 적어 두었습니다. 필요한 기능이 있으면 관리자에게 알려 주세요.",
          "このマニュアルには現在システムにある機能のみ記載しています。必要な機能があれば管理者にお知らせください。",
        ),
      },
    ],
  },
]
